import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, IconButton, Tooltip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Chip,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { format } from 'date-fns';
import { dmsApi } from '../services/dmsApi';
import UploadZone from './UploadZone';
import useDmsPalette from '../hooks/useDmsPalette';

export default function VersionHistory({ documentId, onCompare }) {
  const MP = useDmsPalette();
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);

  const loadVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await dmsApi.getVersions(documentId);
      setVersions(data.versions || data || []);
    } catch (err) {
      setError(err.error || 'Failed to load versions');
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => { loadVersions(); }, [loadVersions]);

  const handleUploadVersion = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      await dmsApi.uploadVersion(documentId, formData);
      setUploadOpen(false);
      setUploadFile(null);
      loadVersions();
    } catch (err) {
      setError(err.error || 'Failed to upload new version');
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadVersion = (versionId) => {
    const token = localStorage.getItem('token');
    const url = `${dmsApi.downloadUrl(documentId)}?version=${versionId}&token=${token}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>;
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="outlined" startIcon={<UploadIcon />} onClick={() => setUploadOpen(true)} size="small">
          Upload New Version
        </Button>
      </Box>

      {versions.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4, color: MP.textSecondary }}>
          <HistoryIcon sx={{ fontSize: 36, mb: 1 }} />
          <Typography variant="body2">No version history available</Typography>
        </Box>
      ) : (
        <Box sx={{ position: 'relative', pl: 3 }}>
          {/* Timeline line */}
          <Box sx={{
            position: 'absolute', left: 10, top: 8, bottom: 8, width: 2,
            bgcolor: MP.border,
          }} />

          {versions.map((ver, idx) => (
            <Box
              key={ver._id || idx}
              sx={{
                position: 'relative', mb: 2, pl: 3,
                '&::before': {
                  content: '""', position: 'absolute', left: -3, top: 8,
                  width: 10, height: 10, borderRadius: '50%',
                  bgcolor: idx === 0 ? MP.blue : MP.textSecondary,
                  border: `2px solid ${MP.surface}`,
                },
              }}
            >
              <Box sx={{
                bgcolor: MP.deepBlue, border: `1px solid ${MP.border}`,
                borderRadius: 1, p: 1.5,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`v${ver.version || ver.versionNumber || idx + 1}`}
                    size="small"
                    sx={{
                      fontWeight: 700, fontSize: '0.7rem',
                      bgcolor: idx === 0 ? `${MP.blue}30` : MP.surface,
                      color: idx === 0 ? MP.blue : MP.textSecondary,
                    }}
                  />
                  {idx === 0 && (
                    <Chip label="Current" size="small"
                      sx={{ height: 18, fontSize: '0.6rem', bgcolor: `${MP.green}20`, color: MP.green }}
                    />
                  )}
                  <Box sx={{ flex: 1 }} />
                  {idx !== 0 && onCompare && (
                    <Tooltip title="Compare with current version">
                      <Button
                        size="small"
                        startIcon={<CompareArrowsIcon />}
                        onClick={() => onCompare(ver)}
                        sx={{ fontSize: '0.7rem', color: MP.textSecondary, borderColor: MP.border, mr: 0.5 }}
                        variant="outlined"
                      >
                        Compare
                      </Button>
                    </Tooltip>
                  )}
                  <Tooltip title="Download this version">
                    <IconButton size="small" onClick={() => handleDownloadVersion(ver._id)}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.8 }}>
                  <PersonIcon sx={{ fontSize: 14, color: MP.textSecondary }} />
                  <Typography variant="caption" color="text.secondary">
                    {ver.changedBy?.name || ver.uploadedBy?.name || 'Unknown'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mx: 0.5 }}>·</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {ver.createdAt ? format(new Date(ver.createdAt), 'MMM d, yyyy · h:mm a') : 'N/A'}
                  </Typography>
                </Box>

                {ver.changelog && (
                  <Typography variant="body2" sx={{ mt: 0.8, color: MP.white, fontSize: '0.8rem' }}>
                    {ver.changelog}
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Upload new version dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload New Version</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <UploadZone onFileDrop={(f) => setUploadFile(f)} />
          {uploadFile && (
            <Typography variant="body2" sx={{ mt: 1, color: MP.green }}>
              Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadOpen(false); setUploadFile(null); }}>Cancel</Button>
          <Button variant="contained" onClick={handleUploadVersion} disabled={!uploadFile || uploading}>
            {uploading ? 'Uploading...' : 'Upload Version'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
